import create from 'zustand';
import useSecuritiesCredentialStore from 'global/Securities/stores/useSecuritiesCredentialStore';
import { ACCOUNT_TYPE } from 'global/TradingModal/RealTradingModal/types';

interface OrderQueueData {
  symbol: string;
  price?: string | number;
  action: 'BUY' | 'SELL';
  orderId?: string;
  status?: string;
}

interface OrderQueueStore {
  visible: boolean;
  symbol: string;
  price?: number;
  action: 'BUY' | 'SELL';
  orderId?: string;
  status?: string;
  pendingOrderData?: OrderQueueData;
  showModal: (data: OrderQueueData) => void;
  hideModal: () => void;
  reset: () => void;
}

/**
 * Convert price string to integer
 * Handles: "2,700" -> 2700, "-" -> undefined, 2700 -> 2700
 */
const convertPriceToNumber = (priceInput?: string | number): number | undefined => {
  if (priceInput === undefined || priceInput === null) {
    return undefined;
  }

  if (typeof priceInput === 'number') {
    return priceInput;
  }

  if (priceInput === '-' || !priceInput) {
    return undefined;
  }

  // Remove comma separator and convert to number
  const cleanPrice = priceInput.replace(/,/g, '');
  const parsedPrice = parseInt(cleanPrice, 10);
  return Number.isNaN(parsedPrice) ? undefined : parsedPrice;
};

const useOrderQueueStore = create<OrderQueueStore>((set, get) => ({
  visible: false,
  symbol: '',
  price: undefined,
  action: 'BUY',
  orderId: undefined,
  status: undefined,
  pendingOrderData: undefined,

  showModal: (data) => {
    // Check if user has trading account
    const { accountType, accessToken } = useSecuritiesCredentialStore.getState();
    const isLoggedIn = !!accessToken;

    // Case 1: User doesn't have any trading account -> Show RegisterTradingModal
    // Only check this if we have explicit info that user has no account
    if (accountType && accountType !== ACCOUNT_TYPE.REAL &&
        accountType !== ACCOUNT_TYPE.VIRTUAL) {
      import('features/securities/AuthModal/store').then(({ default: useAuthModalStore }) => {
        useAuthModalStore.getState().showModal();
      });
      return;
    }

    // Case 2: User has trading account but not logged in -> Show LoginTradingModal
    // This handles both: accountType === null (haven't checked yet) and logged in but no token
    if (!isLoggedIn) {
      // Store the order queue data for later use
      set({
        pendingOrderData: data,
        visible: false,
      });

      import('features/securities/AuthModal/store').then(({ default: useAuthModalStore }) => {
        useAuthModalStore.getState().showModal({
          onRealLoginSuccess: () => {
            // After successful login, proceed with showing order queue
            setTimeout(() => {
              const currentState = get();

              if (currentState.pendingOrderData) {
                // Use the stored data to show modal
                const price = convertPriceToNumber(currentState.pendingOrderData.price);

                // Close TransactionMFModal when order queue modal opens
                import('global/TradingModal/store').then(({ default: tradingModalStore }) => {
                  const tradingModalState = tradingModalStore.getState();
                  if (tradingModalState.onClose) {
                    tradingModalState.onClose();
                  }
                  tradingModalStore.getState().resetModal();
                });

                set({
                  visible: true,
                  symbol: currentState.pendingOrderData.symbol,
                  price,
                  action: currentState.pendingOrderData.action,
                  orderId: currentState.pendingOrderData.orderId,
                  status: currentState.pendingOrderData.status,
                  pendingOrderData: undefined,
                });
              }
            }, 100);
          },
        });
      });
      return;
    }

    // Case 3: User has trading account and is logged in -> Show OrderQueueModal
    const price = convertPriceToNumber(data.price);

    // Close TransactionMFModal when order queue modal opens
    import('global/TradingModal/store').then(({ default: tradingModalStore }) => {
      const tradingModalState = tradingModalStore.getState();
      if (tradingModalState.onClose) {
        tradingModalState.onClose();
      }
      tradingModalStore.getState().resetModal();
    });

    set({
      visible: true,
      symbol: data.symbol,
      price,
      action: data.action,
      orderId: data.orderId,
      status: data.status,
    });
  },

  hideModal: () => {
    set({ visible: false, symbol: '', price: undefined, action: 'BUY', orderId: undefined, status: undefined, pendingOrderData: undefined });
  },

  reset: () => {
    set({ visible: false, symbol: '', price: undefined, action: 'BUY', orderId: undefined, status: undefined, pendingOrderData: undefined });
  },
}));

export default useOrderQueueStore;
